import { MapViewState, PaneState } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import { computed, onMounted, onUnmounted, reactive, Ref, ref, UnwrapRef } from '@vue/composition-api';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  PANE_SIDE_MAX_WIDTH_RATE,
  PANE_SIDE_MIN_WIDTH_RATE,
  VERTICAL_PANE_PADDING_NUMBER,
} from '@/components/ExternalReports/EmergencyResponseTeam/consts';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import { useStore } from '@/hooks/useStore';
import { Settings as UserSettings } from '@/models/apis/user/userResponse';
import type {
  EmergencyResponseTeamCarExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamCar';
import type {
  EmergencyResponseTeamMtxExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMtx';
import type {
  EmergencyResponseTeamReportExt,
  EmergencyResponseTeamReportPhotoExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import type {
  EmergencyResponseTeamSettings,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamSettings';
import { dtFormat } from '@/lib/dateHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/emergencyResponseTeam';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';

interface RedrawCarLayerOptions {
  fitToExtent?: boolean;
  setSelectedCarToCenter?: boolean;
}
export interface UseExtremeMapResult {
  extremeMapRef: Ref;
  mapViewState: UnwrapRef<MapViewState>;
  loadExtremeMapEssentials: () => Promise<void>;
  forceResizeExtremeMap: () => void;
  redrawCarLayer: (options: RedrawCarLayerOptions) => void;
  showEmergencyResponseTeamReportLayer: (selectedReport: EmergencyResponseTeamReportExt, fitToExtent: boolean) => void;
  removeEmergencyResponseTeamReportLayer: () => void;
  showEmergencyResponseTeamCarPopup: (car: EmergencyResponseTeamCarExt) => void;
  showEmergencyResponseTeamReportPhotoPopup: (photo: EmergencyResponseTeamReportPhotoExt) => void;
  showEmergencyResponseTeamMtxPopup: (mtx: EmergencyResponseTeamMtxExt) => void;
  hidePopup: () => void;
}

export function useExtremeMap(paneState: PaneState): UnwrapRef<UseExtremeMapResult> {
  const store = useStore();
  const userState = store.state.user;
  const userSettings = computed<UserSettings>(() => {
    return userState.settings;
  });
  const reportUserSettings = computed<EmergencyResponseTeamSettings | null>(() => {
    const settings = window.master.externalReportSettings?.find(
      setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
    );
    if (!settings) return null;
    if (!isEmergencyResponseTeamExternalReportSettings(settings)) return null;

    return settings;
  });

  const extremeMapRef = ref<InstanceType<typeof ExtremeMap>>();

  const mapViewState = reactive<MapViewState>({
    extremeMapEssentials: null,
    onResizeFunc: () => {},
  });

  const isEmergencyResponseTeamExternalReportSettings = (
    settings: ExternalReportSettings,
  ): settings is EmergencyResponseTeamSettings => {
    return !!settings && settings.external_report_type === EXTERNAL_REPORT_TYPE;
  };

  const loadExtremeMapEssentials = async(): Promise<void> => {
    await waitForUserAndMasters();
    mapViewState.extremeMapEssentials = {
      userSettings: userSettings.value,
      kpMap: window.master.kpMap,
      externalReportSettings: reportUserSettings.value,
    };
  };

  const resizeMap = (mapHeight: number) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.setMapHeight(mapHeight);
    extremeMapRef.value.triggerResize();
  };

  const initResizeFunc = () => {
    mapViewState.onResizeFunc = () => {
      const headerH = 57;
      const searchBarH = 96;
      const h = window.innerHeight - headerH - searchBarH;
      const w = window.innerWidth - VERTICAL_PANE_PADDING_NUMBER;
      const paneStyle = paneState.paneStyle;
      paneStyle.paneSideMinWidth = Math.min(Math.floor(w * PANE_SIDE_MIN_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.paneSideMaxWidth = Math.min(Math.floor(w * PANE_SIDE_MAX_WIDTH_RATE), PANE_SIDE_HARD_MAX_WIDTH_PX);
      paneStyle.listMinHeight = Math.floor(h * (paneState.paneStyleLimits?.listMinHeightRate ?? 1.0));
      paneStyle.listMaxHeight = Math.floor(h * (paneState.paneStyleLimits?.listMaxHeightRate ?? 1.0));
      paneState.paneStyle = paneStyle;
      resizeMap(h);
    };
    mapViewState.onResizeFunc();
    window.addEventListener('resize', mapViewState.onResizeFunc);
  };

  const forceResizeExtremeMap = (): void => {
    mapViewState.onResizeFunc();
  };

  const redrawCarLayer = ({ fitToExtent, setSelectedCarToCenter }: RedrawCarLayerOptions = {}) => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.redrawExternalCarLayer({
      hideCarIcons: !paneState.showCarIcons,
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: !!fitToExtent,
      setSelectedCarToCenter,
      externalReportUserSettings: reportUserSettings.value,
      showDirectionArrow: false,
    });
  };

  const getEmergencyResponseTeamCarPopupContentInnerHtml = (car: EmergencyResponseTeamCarExt): string => {
    const pcLabels = reportUserSettings.value?.pc;
    if (!pcLabels) return '';

    console.log('pcLabels:', pcLabels);
    let html = `<p class="title">【${car.hanName}】</p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels.field_label.company_name}:</span>
        <span class="val ml2">${car.companyName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels.field_label.status}:</span>
        <span class="val ml2">${car.statusDisp}</span>
      </p>`;
    if (!car.isMoving || !car.reportExt) {
      return html;
    }

    html += `
      <p class="info-row">
        <span class="val">${pcLabels.table_header.start_work_ts}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationStartTs, 'HH:MM:SS')}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels.table_header.end_work_ts}:</span>
        <span class="val ml2">${dtFormat(car.reportExt.operationEndTs, 'HH:MM:SS')}</span>
      </p>`;
    return html;
  };

  const getEmergencyResponseTeamReportInfoContentInnerHtml = (info: EmergencyResponseTeamReportPhotoExt | EmergencyResponseTeamMtxExt): string => {
    const pcLabels = reportUserSettings.value?.pc;
    if (!pcLabels) return '';

    let html = `
      <p class="info-row">
        <span class="val">${pcLabels.field_label.company_name}:</span>
        <span class="val ml2">${info.companyName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels.field_label.han_name}:</span>
        <span class="val ml2">${info.hanName}</span>
      </p>`;
    html += `
      <p class="info-row">
        <span class="val">${pcLabels.field_label.work_time}:</span>
        <span class="val ml2">${dtFormat(info.ts)}</span>
      </p>`;
    return html;
  };

  const getEmergencyResponseTeamReportPhotoPopupContentInnerHtml = (photo: EmergencyResponseTeamReportPhotoExt): string => {
    const photoTypeDisp = photo.photoType ?? '';
    let html =
      `<p class="title">【${photoTypeDisp}写真${photo.dispOrder}】</p>`;
    html += getEmergencyResponseTeamReportInfoContentInnerHtml(photo);
    html += `
      <div class="image-area" id="external-map-photo${photo.id}">
        <div class="image-box">
          <div class="image-wrap">
            <img src="${photo.savedImage}" class="image" />
          </div>
        </div>
      </div>`;
    return html;
  };

  const showEmergencyResponseTeamReportLayer = (selectedReport: EmergencyResponseTeamReportExt, fitToExtent: boolean): void => {
    if (!extremeMapRef.value) return;

    extremeMapRef.value.showExternalReportLayer(selectedReport, {
      photoTypeVisibility: paneState.photoTypeVisibility,
      fitToExtent: fitToExtent,
      externalReportUserSettings: reportUserSettings.value,
      showTrack: true,
    });
  };

  const removeEmergencyResponseTeamReportLayer = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.removeExternalReportLayer();
  };

  const showEmergencyResponseTeamCarPopup = (car: EmergencyResponseTeamCarExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getEmergencyResponseTeamCarPopupContentInnerHtml(car);
    extremeMapRef.value.showExternalCarPopup(car, innerHTML);
  };

  const showEmergencyResponseTeamReportPhotoPopup = (photo: EmergencyResponseTeamReportPhotoExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getEmergencyResponseTeamReportPhotoPopupContentInnerHtml(photo);
    extremeMapRef.value.showExternalReportPhotoPopup(photo, innerHTML);
  };

  const showEmergencyResponseTeamMtxPopup = (mtx: EmergencyResponseTeamMtxExt): void => {
    if (!extremeMapRef.value) return;

    const innerHTML = getEmergencyResponseTeamReportInfoContentInnerHtml(mtx);
    extremeMapRef.value.showExternalMtxPopup(mtx, innerHTML);
  };

  const hidePopup = (): void => {
    if (!extremeMapRef.value) return;
    extremeMapRef.value.hidePopup();
  };

  onMounted(async() => {
    initResizeFunc();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', mapViewState.onResizeFunc);
  });

  return {
    extremeMapRef,
    mapViewState,
    loadExtremeMapEssentials,
    forceResizeExtremeMap,
    redrawCarLayer,
    showEmergencyResponseTeamReportLayer,
    removeEmergencyResponseTeamReportLayer,
    showEmergencyResponseTeamCarPopup,
    showEmergencyResponseTeamReportPhotoPopup,
    showEmergencyResponseTeamMtxPopup,
    hidePopup,
  };
}
